import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import {
  ArrowOutwardRounded,
  AutoAwesomeRounded,
  Close,
  DashboardCustomizeRounded,
  DevicesRounded,
  DragIndicatorRounded,
  EditRounded,
  Inventory2Rounded,
  PrecisionManufacturingRounded,
  RefreshRounded,
  SaveRounded,
  SettingsBackupRestoreRounded,
  UpdateRounded,
  WidgetsRounded,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../../auth/useAuth";
import { can } from "../../auth/can";

import { APP_API_BASE } from "../../config/api";
import Zoom from "../zoomBrowser/Zoom";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") || `${APP_API_BASE}`;

const WIDGETS = [
  {
    id: "fleet",
    title: "Device Command",
    description: "Live device KPIs and availability.",
    permission: "device.view",
    span: 8,
  },
  {
    id: "actions",
    title: "Quick Actions",
    description: "Operator shortcuts.",
    permission: null,
    span: 4,
  },
  {
    id: "families",
    title: "Device Families",
    description: "Top device types and counts.",
    permission: "device.view",
    span: 5,
  },
  {
    id: "attention",
    title: "Attention Queue",
    description: "Devices needing intervention.",
    permission: "device.view",
    span: 7,
  },
  {
    id: "inventory",
    title: "Inventory Snapshot",
    description: "Asset and ownership visibility.",
    permission: "inventory.asset.view",
    span: 4,
  },
  {
    id: "manufacturing",
    title: "Manufacturing Snapshot",
    description: "Partners, products, and production orders.",
    permission: "inventory.asset.view",
    span: 4,
  },
  {
    id: "projects",
    title: "Project Rollouts",
    description: "Customers, deployment programs, and site readiness.",
    permission: "inventory.asset.view",
    span: 4,
  },
  {
    id: "firmware",
    title: "Firmware Rollouts",
    description: "Deployment activity and posture.",
    permission: "firmware.view",
    span: 4,
  },
  {
    id: "activity",
    title: "Recent Activity",
    description: "Latest audit-style actions.",
    permission: "audit.view",
    span: 4,
  },
];

const DEFAULT_ORDER = WIDGETS.map((widget) => widget.id);
const FAMILY_COLORS = ["#2563EB", "#0F766E", "#7C3AED", "#EA580C", "#DC2626"];
const STATUS_COLORS = ["#2563EB", "#22C55E", "#F97316"];
const REQUEST_TIMEOUT_MS = 8000;

const formatRelative = (value) => {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  const mins = Math.floor((Date.now() - date.getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day ago`;
  return date.toLocaleDateString();
};

const reorder = (items, draggedId, targetId) => {
  if (!draggedId || !targetId || draggedId === targetId) return items;
  const from = items.indexOf(draggedId);
  const to = items.indexOf(targetId);
  if (from === -1 || to === -1) return items;
  const next = [...items];
  next.splice(from, 1);
  next.splice(to, 0, draggedId);
  return next;
};

const withTimeout = (promise, timeoutMs = REQUEST_TIMEOUT_MS) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), timeoutMs),
    ),
  ]);

const MetricTile = ({ label, value, helper, background, color }) => (
  <Box
    sx={{
      p: 1.75,
      borderRadius: 3,
      background,
      border: "1px solid rgba(15,23,42,0.06)",
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 1,
        fontSize: 32,
        fontWeight: 900,
        letterSpacing: "-0.05em",
        color,
      }}
    >
      {value}
    </Typography>
    <Typography sx={{ mt: 1, fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>
      {helper}
    </Typography>
  </Box>
);

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.9,
        borderRadius: 2,
        backgroundColor: "rgba(15,23,42,0.92)",
      }}
    >
      <Typography sx={{ color: "#F8FAFC", fontSize: 13, fontWeight: 800 }}>
        {payload[0]?.name}: {payload[0]?.value ?? "--"}
      </Typography>
    </Box>
  );
};

const WidgetCard = ({
  title,
  subtitle,
  action,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  children,
}) => (
  <Card
    draggable={draggable}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    sx={{
      height: "100%",
      width: "100%",
      borderRadius: 5,
      border: "1px solid rgba(15,23,42,0.08)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
      boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
      overflow: "hidden",
    }}
  >
    <CardHeader
      sx={{
        px: 2.5,
        py: 2,
        borderBottom: "1px solid rgba(226,232,240,0.8)",
        background:
          "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
      }}
      title={
        <Stack
          direction={{ xs: "column", sm: "row" }} // 📱 column → 💻 row
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
        >
          {/* LEFT */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: { xs: 18, sm: 21 }, // 📱 smaller title
                fontWeight: 800,
                color: "#fff",
                letterSpacing: "-0.03em",
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                color: "#dddddd",
                fontSize: 13,
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* RIGHT ACTION */}
          <Box
            sx={{
              width: { xs: "100%", sm: "auto" }, // 📱 full width
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {action}
          </Box>
        </Stack>
      }
    />
    <CardContent sx={{ p: 0, height: "100%" }}>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </CardContent>
  </Card>
);

export default function Overview() {
  const navigate = useNavigate();
  const { permissions, activeOrg, user, organizations } = useAuth();
  const isPlatformOwner = can(permissions, "system.manage");
  const isSuperAdmin = user?.global_role === "superadmin";
  const isFirstTimePlatformSetup = isSuperAdmin && organizations.length === 0;
  const canViewStats = can(permissions, "stats.view");
  const canViewDevices = can(permissions, "device.view");
  const canViewInventory = can(permissions, "inventory.asset.view");
  const canViewManufacturing = can(permissions, "inventory.asset.view");
  const canViewProjects =
    isSuperAdmin && can(permissions, "inventory.asset.view");
  const canViewFirmware = can(permissions, "firmware.view");
  const canViewAudit = can(permissions, "audit.view");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [draggedWidgetId, setDraggedWidgetId] = useState("");
  const [widgetOrder, setWidgetOrder] = useState([]);
  const [hiddenWidgets, setHiddenWidgets] = useState([]);
  const [data, setData] = useState({
    stats: null,
    devices: [],
    deviceTypes: [],
    inventory: null,
    manufacturing: null,
    projects: null,
    deployments: [],
    audit: [],
  });

  const availableWidgets = useMemo(
    () =>
      WIDGETS.filter(
        (widget) =>
          (!widget.permission || can(permissions, widget.permission)) &&
          !(widget.id === "projects" && !isSuperAdmin),
      ),
    [isSuperAdmin, permissions],
  );

  const layoutKey = useMemo(
    () => `overview-layout:${activeOrg?.id || "global"}`,
    [activeOrg?.id],
  );

  useEffect(() => {
    const allowedIds = availableWidgets.map((widget) => widget.id);
    const savedRaw = localStorage.getItem(layoutKey);

    if (savedRaw) {
      try {
        const saved = JSON.parse(savedRaw);
        const savedOrder = Array.isArray(saved.order) ? saved.order : [];
        setWidgetOrder([
          ...savedOrder.filter((id) => allowedIds.includes(id)),
          ...allowedIds.filter((id) => !savedOrder.includes(id)),
        ]);
        setHiddenWidgets(
          Array.isArray(saved.hidden)
            ? saved.hidden.filter((id) => allowedIds.includes(id))
            : [],
        );
        return;
      } catch (error) {
        console.error("Failed to parse overview layout", error);
      }
    }

    setWidgetOrder(DEFAULT_ORDER.filter((id) => allowedIds.includes(id)));
    setHiddenWidgets([]);
  }, [availableWidgets, layoutKey]);

  useEffect(() => {
    if (!widgetOrder.length) return;
    localStorage.setItem(
      layoutKey,
      JSON.stringify({ order: widgetOrder, hidden: hiddenWidgets }),
    );
  }, [hiddenWidgets, layoutKey, widgetOrder]);

  const headers = useMemo(() => {
    const token = localStorage.getItem("authToken");
    return {
      Authorization: `Bearer ${token}`,
      ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
    };
  }, [activeOrg?.id]);

  const loadData = async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const coreResults = await Promise.allSettled([
        canViewStats
          ? withTimeout(
              axios.get(`${API_BASE}/api/v1/devices/stats`, { headers }),
            )
          : Promise.resolve({ data: null }),
        canViewDevices
          ? withTimeout(axios.get(`${API_BASE}/api/v1/devices`, { headers }))
          : Promise.resolve({ data: [] }),
        canViewDevices
          ? withTimeout(
              axios.get(`${API_BASE}/api/v1/device-types`, { headers }),
            )
          : Promise.resolve({ data: [] }),
      ]);

      setData((prev) => ({
        ...prev,
        stats:
          coreResults[0].status === "fulfilled"
            ? coreResults[0].value.data
            : prev.stats,
        devices:
          coreResults[1].status === "fulfilled" &&
          Array.isArray(coreResults[1].value.data)
            ? coreResults[1].value.data
            : prev.devices,
        deviceTypes:
          coreResults[2].status === "fulfilled" &&
          Array.isArray(coreResults[2].value.data)
            ? coreResults[2].value.data
            : prev.deviceTypes,
      }));

      setLoading(false);
      setSecondaryLoading(true);

      const secondaryResults = await Promise.allSettled([
        canViewInventory
          ? withTimeout(
              axios.get(
                `${API_BASE}/api/v1/inventory/assets?page=1&per_page=100`,
                {
                  headers,
                },
              ),
            )
          : Promise.resolve({ data: null }),
        canViewManufacturing
          ? withTimeout(
              Promise.all([
                axios.get(`${API_BASE}/api/v1/manufacturing/partners`, {
                  headers,
                }),
                axios.get(`${API_BASE}/api/v1/manufacturing/products`, {
                  headers,
                }),
                axios.get(`${API_BASE}/api/v1/manufacturing/orders`, {
                  headers,
                }),
              ]),
            )
          : Promise.resolve({ data: null }),
        canViewProjects
          ? withTimeout(
              Promise.all([
                axios.get(`${API_BASE}/api/v1/projects/summary`, { headers }),
                axios.get(`${API_BASE}/api/v1/projects/deployments`, {
                  headers,
                }),
                axios.get(`${API_BASE}/api/v1/projects/sites`, { headers }),
              ]),
            )
          : Promise.resolve({ data: null }),
        canViewFirmware
          ? withTimeout(
              axios.get(`${API_BASE}/api/v1/firmware/deployments`, { headers }),
            )
          : Promise.resolve({ data: [] }),
        canViewAudit
          ? withTimeout(axios.get(`${API_BASE}/api/v1/audit-logs`, { headers }))
          : Promise.resolve({ data: [] }),
      ]);

      setData((prev) => ({
        ...prev,
        inventory:
          secondaryResults[0].status === "fulfilled"
            ? secondaryResults[0].value.data
            : prev.inventory,
        manufacturing:
          secondaryResults[1].status === "fulfilled" &&
          Array.isArray(secondaryResults[1].value)
            ? {
                partners: secondaryResults[1].value[0]?.data?.partners || [],
                products: secondaryResults[1].value[1]?.data?.products || [],
                orders: secondaryResults[1].value[2]?.data?.orders || [],
              }
            : prev.manufacturing,
        projects:
          secondaryResults[2].status === "fulfilled" &&
          Array.isArray(secondaryResults[2].value)
            ? {
                summary: secondaryResults[2].value[0]?.data || null,
                projects: secondaryResults[2].value[1]?.data?.projects || [],
                sites: secondaryResults[2].value[2]?.data?.sites || [],
              }
            : prev.projects,
        deployments:
          secondaryResults[3].status === "fulfilled" &&
          Array.isArray(secondaryResults[3].value.data)
            ? secondaryResults[3].value.data
            : prev.deployments,
        audit:
          secondaryResults[4].status === "fulfilled" &&
          Array.isArray(secondaryResults[4].value.data)
            ? secondaryResults[4].value.data.slice(0, 7)
            : prev.audit,
      }));
    } finally {
      setLoading(false);
      setRefreshing(false);
      setSecondaryLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [
    activeOrg?.id,
    canViewStats,
    canViewDevices,
    canViewInventory,
    canViewManufacturing,
    canViewProjects,
    canViewFirmware,
    canViewAudit,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = data.stats || {
    total_devices: 0,
    active_devices: 0,
    inactive_devices: 0,
    device_types: 0,
  };
  const inventoryAssets = Array.isArray(data.inventory?.data)
    ? data.inventory.data
    : [];
  const onlineRate = stats.total_devices
    ? Math.round((stats.active_devices / stats.total_devices) * 100)
    : 0;

  const familyChart = useMemo(() => {
    const counts = new Map();

    data.deviceTypes.forEach((item) => {
      counts.set(item.id, {
        name: item.name,
        count: item.device_count || 0,
        id: item.id,
        schema: item.config_summary?.schema_fields || 0,
      });
    });

    data.devices.forEach((device) => {
      const typeId = device.device_type?.id || device.device_type_id;
      const typeName = device.device_type?.name || "Custom Device";
      if (!typeId) return;

      const current = counts.get(typeId) || {
        id: typeId,
        name: typeName,
        count: 0,
        schema: 0,
      };
      current.count += 1;
      counts.set(typeId, current);
    });

    return [...counts.values()]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, 6);
  }, [data.deviceTypes, data.devices]);

  const attentionDevices = useMemo(() => {
    const inactive = data.devices
      .filter((device) => !device.is_active)
      .map((device) => ({ ...device, reason: "Inactive" }));
    const stale = data.devices
      .filter(
        (device) =>
          device.is_active &&
          device.last_seen &&
          Date.now() - new Date(device.last_seen).getTime() >
            24 * 60 * 60 * 1000,
      )
      .map((device) => ({ ...device, reason: "No heartbeat in 24h" }));
    const combined = [...inactive, ...stale];
    const seen = new Set();
    return combined
      .filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      })
      .slice(0, 6);
  }, [data.devices]);

  const inventorySummary = useMemo(
    () => ({
      total:
        data.inventory?.meta?.pagination?.total_items ||
        inventoryAssets.length ||
        0,
      inStock: inventoryAssets.filter((asset) => asset.status === "IN_STOCK")
        .length,
      deployed: inventoryAssets.filter((asset) => asset.status === "DEPLOYED")
        .length,
      customerOwned: inventoryAssets.filter(
        (asset) => asset.ownership_state === "OWNED_BY_CUSTOMER",
      ).length,
    }),
    [data.inventory, inventoryAssets],
  );

  const firmwareSummary = useMemo(() => {
    const grouped = data.deployments.reduce((acc, deployment) => {
      const key = String(deployment.status || "UNKNOWN").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return {
      total: data.deployments.length,
      active:
        (grouped.active || 0) +
        (grouped.running || 0) +
        (grouped.in_progress || 0),
      pending: grouped.pending || 0,
      completed: grouped.completed || 0,
      latest: data.deployments.slice(0, 5),
    };
  }, [data.deployments]);

  const manufacturingSummary = useMemo(() => {
    const partners = Array.isArray(data.manufacturing?.partners)
      ? data.manufacturing.partners
      : [];
    const products = Array.isArray(data.manufacturing?.products)
      ? data.manufacturing.products
      : [];
    const orders = Array.isArray(data.manufacturing?.orders)
      ? data.manufacturing.orders
      : [];
    const released = orders.filter((order) =>
      ["RELEASED", "IN_PRODUCTION", "PARTIALLY_COMPLETED"].includes(
        order.status,
      ),
    ).length;
    const plannedUnits = orders.reduce(
      (sum, order) => sum + Number(order.target_quantity || 0),
      0,
    );

    return {
      partners,
      products,
      orders,
      totalPartners: partners.length,
      activePartners: partners.filter((partner) => partner.status === "ACTIVE")
        .length,
      totalProducts: products.length,
      activeProducts: products.filter((product) => product.is_active).length,
      totalOrders: orders.length,
      releasedOrders: released,
      plannedUnits,
      latestOrders: orders.slice(0, 4),
    };
  }, [data.manufacturing]);

  const projectsSummary = useMemo(() => {
    const summary = data.projects?.summary || {};
    const projectItems = Array.isArray(data.projects?.projects)
      ? data.projects.projects
      : [];
    const siteItems = Array.isArray(data.projects?.sites)
      ? data.projects.sites
      : [];

    return {
      customers: Number(summary.customers || 0),
      projects: Number(summary.projects || projectItems.length || 0),
      sites: Number(summary.sites || siteItems.length || 0),
      plannedDevices: Number(summary.planned_devices || 0),
      commissionedDevices: Number(summary.commissioned_devices || 0),
      activeProjects: Number(summary.active_projects || 0),
      liveSites: siteItems.filter((site) => site.status === "LIVE").length,
      readySites: siteItems.filter((site) =>
        ["READY", "IN_PROGRESS"].includes(site.status),
      ).length,
      latestProjects: projectItems.slice(0, 4),
    };
  }, [data.projects]);

  const visibleWidgets = useMemo(
    () =>
      widgetOrder
        .map((id) => availableWidgets.find((widget) => widget.id === id))
        .filter(Boolean)
        .filter((widget) => !hiddenWidgets.includes(widget.id)),
    [availableWidgets, hiddenWidgets, widgetOrder],
  );

  const resetLayout = () => {
    const allowedIds = availableWidgets.map((widget) => widget.id);
    setWidgetOrder(DEFAULT_ORDER.filter((id) => allowedIds.includes(id)));
    setHiddenWidgets([]);
  };

  const toggleWidget = (widgetId) => {
    setHiddenWidgets((prev) =>
      prev.includes(widgetId)
        ? prev.filter((id) => id !== widgetId)
        : [...prev, widgetId],
    );
  };

  const dragBadge = editMode ? (
    <Chip
      icon={<DragIndicatorRounded fontSize="small" color="#fff" />}
      label="Drag"
      size="small"
      sx={{ fontWeight: 700, color: "#fff" }}
    />
  ) : null;

  const renderWidget = (widgetId) => {
    if (widgetId === "fleet") {
      return (
        <WidgetCard
          title="Fleet Command"
          subtitle="Live device KPIs and availability for the active workspace."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                fullWidth={false}
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/device_onboarding")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                Open devices
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={110} />
              <Skeleton variant="rounded" height={220} />
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                <MetricTile
                  label="Total Devices"
                  value={stats.total_devices}
                  helper="All mapped devices in the active org."
                  background="linear-gradient(180deg, rgba(219,234,254,0.96), rgba(255,255,255,0.92))"
                  color="#1D4ED8"
                />
                <MetricTile
                  label="Active Devices"
                  value={`${onlineRate}%`}
                  helper={`${stats.active_devices} devices active and reporting.`}
                  background="linear-gradient(180deg, rgba(220,252,231,0.96), rgba(255,255,255,0.92))"
                  color="#15803D"
                />
                <MetricTile
                  label="Inactive Devices"
                  value={stats.inactive_devices}
                  helper="Devices awaiting intervention."
                  background="linear-gradient(180deg, rgba(255,237,213,0.96), rgba(255,255,255,0.92))"
                  color="#C2410C"
                />
                <MetricTile
                  label="Device Families"
                  value={stats.device_types}
                  helper="Configured types across the devices."
                  background="linear-gradient(180deg, rgba(237,233,254,0.96), rgba(255,255,255,0.92))"
                  color="#6D28D9"
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    border: "1px solid rgba(226,232,240,0.8)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                    Availability
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}>
                    Overall operational readiness for{" "}
                    {activeOrg?.name || "the workspace"}.
                  </Typography>
                  <Box sx={{ mt: 2.5 }}>
                    <LinearProgress
                      variant="determinate"
                      value={onlineRate}
                      sx={{
                        height: 12,
                        borderRadius: 999,
                        backgroundColor: "rgba(226,232,240,0.9)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          background:
                            "linear-gradient(90deg, #2563EB 0%, #0F766E 100%)",
                        },
                      }}
                    />
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mt: 1.1 }}
                    >
                      <Typography sx={{ color: "#64748B", fontSize: 13 }}>
                        {stats.active_devices} active
                      </Typography>
                      <Typography
                        sx={{ color: "#0F172A", fontSize: 13, fontWeight: 800 }}
                      >
                        {onlineRate}% available
                      </Typography>
                    </Stack>
                  </Box>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    border: "1px solid rgba(226,232,240,0.8)",
                    backgroundColor: "#fff",
                    height: 240,
                  }}
                >
                  <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                    Device Types
                  </Typography>
                  <Box sx={{ mt: 1.5, height: 175 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Active", value: stats.active_devices },
                            { name: "Inactive", value: stats.inactive_devices },
                            { name: "Types", value: stats.device_types },
                          ]}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={48}
                          outerRadius={72}
                          paddingAngle={4}
                        >
                          {STATUS_COLORS.map((color) => (
                            <Cell key={color} fill={color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<ChartTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            </Stack>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "actions") {
      const actions = [
        {
          label: "Open Devices",
          helper: "Provision and manage devices",
          icon: <DevicesRounded fontSize="small" />,
          permission: "device.view",
          onClick: () => navigate("/device_onboarding"),
        },
        {
          label: "Inventory",
          helper: "View assets and bindings",
          icon: <Inventory2Rounded fontSize="small" />,
          permission: "inventory.asset.view",
          onClick: () => navigate("/inventory/overview"),
        },
        {
          label: "Manufacturing",
          helper: "Partners, products, and orders",
          icon: <PrecisionManufacturingRounded fontSize="small" />,
          permission: "inventory.asset.view",
          onClick: () => navigate("/manufacturing/overview"),
        },
        {
          label: "Projects",
          helper: "Deployment customers, programs, and sites",
          icon: <DashboardCustomizeRounded fontSize="small" />,
          permission: "inventory.asset.view",
          visible: isSuperAdmin,
          onClick: () => navigate("/projects/overview"),
        },
        {
          label: isPlatformOwner ? "Device Types" : "Fleet Dashboards",
          helper: isPlatformOwner
            ? "Configure schema and dashboards"
            : "View device-family dashboards",
          icon: <WidgetsRounded fontSize="small" />,
          permission: isPlatformOwner ? "system.manage" : "device.view",
          onClick: () =>
            navigate(
              isPlatformOwner
                ? "/device_onboarding/devicetypes"
                : "/device_onboarding",
            ),
        },
        {
          label: "Firmware",
          helper: "Track rollouts and packages",
          icon: <UpdateRounded fontSize="small" />,
          permission: "system.manage",
          onClick: () => navigate("/ota/deployments"),
        },
      ];
      const visibleActions = actions.filter(
        (action) =>
          (action.visible ?? true) && can(permissions, action.permission),
      );

      return (
        <WidgetCard
          title="Quick Actions"
          subtitle="Shortcuts into the most-used operational flows."
          action={dragBadge}
        >
          {visibleActions.length ? (
            <Stack spacing={1.25}>
              {visibleActions.map((action) => (
                <Button
                  key={action.label}
                  fullWidth
                  variant="outlined"
                  startIcon={action.icon}
                  endIcon={<ArrowOutwardRounded />}
                  onClick={action.onClick}
                  sx={{
                    justifyContent: "space-between",
                    textTransform: "none",
                    borderRadius: 3,
                    px: 1.8,
                    py: 1.4,
                    borderColor: "rgba(15,23,42,0.08)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: "left" }}>
                    <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                      {action.label}
                    </Typography>
                    <Typography
                      sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                    >
                      {action.helper}
                    </Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          ) : (
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px dashed rgba(148,163,184,0.7)",
                backgroundColor: "#F8FAFC",
              }}
            >
              <Typography sx={{ color: "#0F172A", fontWeight: 700 }}>
                No quick actions are available yet.
              </Typography>
              <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 13 }}>
                Your account does not currently have workspace permissions. Ask
                a superadmin to assign your organization role.
              </Typography>
            </Box>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "families") {
      return (
        <WidgetCard
          title="Device Families"
          subtitle="Top device types and quick launch into family dashboards."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              {isPlatformOwner && (
                <Button
                  variant="text"
                  endIcon={<ArrowOutwardRounded />}
                  onClick={() => navigate("/device_onboarding/devicetypes")}
                  sx={{ textTransform: "none", fontWeight: 700, color: "#fff" }}
                >
                  View all
                </Button>
              )}
            </Stack>
          }
        >
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : familyChart.length === 0 ? (
            <Typography sx={{ color: "#94A3B8" }}>
              No device type data yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={familyChart}
                    margin={{ top: 8, right: 8, left: -10, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 4"
                      vertical={false}
                      stroke="rgba(148,163,184,0.16)"
                    />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#64748B" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#64748B" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <RechartsTooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {familyChart.map((item, index) => (
                        <Cell
                          key={item.id}
                          fill={FAMILY_COLORS[index % FAMILY_COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              <Stack spacing={1}>
                {familyChart.slice(0, 3).map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1.35,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.85)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                        >
                          {item.count} devices - {item.schema} schema fields
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          navigate(
                            `/device_onboarding/devicetypes/${item.id}/dashboard`,
                          )
                        }
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        Open
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "attention") {
      return (
        <WidgetCard
          title="Attention Queue"
          subtitle="The devices most likely to require action next."
          action={
            <Stack direction="row" spacing={1}>
              <Chip
                label={`${attentionDevices.length} flagged`}
                size="small"
                sx={{
                  fontWeight: 700,
                  color: attentionDevices.length ? "#B42318" : "#166534",
                  backgroundColor: attentionDevices.length
                    ? "#FEF3F2"
                    : "#ECFDF3",
                }}
              />
              {dragBadge}
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.2}>
              {[...Array(4)].map((_, index) => (
                <Skeleton key={index} variant="rounded" height={72} />
              ))}
            </Stack>
          ) : attentionDevices.length === 0 ? (
            <Typography sx={{ color: "#94A3B8" }}>
              No devices currently require urgent attention.
            </Typography>
          ) : (
            <Stack spacing={1.2}>
              {attentionDevices.map((device) => (
                <Box
                  key={device.id}
                  sx={{
                    p: 1.4,
                    borderRadius: 3.5,
                    border: "1px solid rgba(254,202,202,0.9)",
                    background:
                      "linear-gradient(180deg, rgba(254,242,242,0.92), rgba(255,255,255,0.98))",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                        {device.name}
                      </Typography>
                      <Typography
                        sx={{ mt: 0.3, color: "#64748B", fontSize: 12 }}
                      >
                        {device.device_type?.name || "Unassigned type"} -{" "}
                        {device.last_seen
                          ? `Last seen ${formatRelative(device.last_seen)}`
                          : "No heartbeat recorded"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={device.reason}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: "#B42318",
                          backgroundColor: "#FEE4E2",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`/devices/${device.id}`, {
                            state: { device },
                          })
                        }
                      >
                        <ArrowOutwardRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "inventory") {
      return (
        <WidgetCard
          title="Inventory Snapshot"
          subtitle="Asset counts and operational ownership from inventory."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/inventory/overview")}
                sx={{ textTransform: "none", fontWeight: 700, color: "#fff" }}
              >
                Open inventory
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          ) : data.inventory ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.25,
                }}
              >
                {[
                  [
                    "Total Assets",
                    inventorySummary.total,
                    "#EFF6FF",
                    "#1D4ED8",
                  ],
                  ["In Stock", inventorySummary.inStock, "#ECFDF3", "#15803D"],
                  ["Deployed", inventorySummary.deployed, "#FFF7ED", "#C2410C"],
                  [
                    "Customer Owned",
                    inventorySummary.customerOwned,
                    "#F5F3FF",
                    "#6D28D9",
                  ],
                ].map(([label, value, bg, tone]) => (
                  <Box
                    key={label}
                    sx={{ p: 1.4, borderRadius: 3, backgroundColor: bg }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.9,
                        fontSize: 28,
                        fontWeight: 900,
                        color: tone,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Stack spacing={1}>
                {inventoryAssets.slice(0, 4).map((asset) => (
                  <Box
                    key={asset.id}
                    sx={{
                      p: 1.3,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.85)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                          {asset.serial_number}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                        >
                          {asset.asset_type} - {asset.model || "Model pending"}
                        </Typography>
                      </Box>
                      <Chip
                        label={asset.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: "#0F4C81",
                          backgroundColor: "#EFF6FF",
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Typography sx={{ color: "#94A3B8" }}>
              Inventory data is unavailable for this access scope.
            </Typography>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "firmware") {
      return (
        <WidgetCard
          title="Firmware Rollouts"
          subtitle="Deployment posture and latest rollout movement."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/ota/deployments")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                View rollouts
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          ) : data.deployments.length === 0 ? (
            <Typography sx={{ color: "#94A3B8" }}>
              No firmware deployments available yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.25,
                }}
              >
                {[
                  ["Total", firmwareSummary.total, "#EFF6FF", "#1D4ED8"],
                  ["Active", firmwareSummary.active, "#ECFDF3", "#15803D"],
                  ["Pending", firmwareSummary.pending, "#FFF7ED", "#C2410C"],
                  [
                    "Completed",
                    firmwareSummary.completed,
                    "#F5F3FF",
                    "#6D28D9",
                  ],
                ].map(([label, value, bg, tone]) => (
                  <Box
                    key={label}
                    sx={{ p: 1.4, borderRadius: 3, backgroundColor: bg }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.9,
                        fontSize: 28,
                        fontWeight: 900,
                        color: tone,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Stack spacing={1}>
                {firmwareSummary.latest.map((deployment) => (
                  <Box
                    key={deployment.id}
                    sx={{
                      p: 1.3,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.85)",
                      backgroundColor: "#fff",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                          {deployment.device_name || "Deployment"}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                        >
                          Created {formatRelative(deployment.created_at)}
                        </Typography>
                      </Box>
                      <Chip
                        label={deployment.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          color: "#0F4C81",
                          backgroundColor: "#EFF6FF",
                        }}
                      />
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "manufacturing") {
      return (
        <WidgetCard
          title="Manufacturing Snapshot"
          subtitle="Operational visibility into partners, products, and production orders."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/manufacturing/overview")}
                sx={{ textTransform: "none", fontWeight: 700, color: "#fff" }}
              >
                Open manufacturing
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          ) : data.manufacturing ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.25,
                }}
              >
                {[
                  [
                    "Partners",
                    manufacturingSummary.totalPartners,
                    "#EFF6FF",
                    "#1D4ED8",
                  ],
                  [
                    "Products",
                    manufacturingSummary.totalProducts,
                    "#ECFDF3",
                    "#15803D",
                  ],
                  [
                    "Orders",
                    manufacturingSummary.totalOrders,
                    "#FFF7ED",
                    "#C2410C",
                  ],
                  [
                    "Units Planned",
                    manufacturingSummary.plannedUnits,
                    "#F5F3FF",
                    "#6D28D9",
                  ],
                ].map(([label, value, bg, tone]) => (
                  <Box
                    key={label}
                    sx={{ p: 1.4, borderRadius: 3, backgroundColor: bg }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.9,
                        fontSize: 28,
                        fontWeight: 900,
                        color: tone,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr", // 📱 mobile → 1 column
                    sm: "1fr 1fr", // tablet → 2 columns
                    md: "1fr 1fr", // desktop → 2 columns
                  },
                  gap: 1.25,
                }}
              >
                <MetricTile
                  label="Active Partners"
                  value={manufacturingSummary.activePartners}
                  helper="Approved vendors ready for work"
                  background="#F8FAFC"
                  color="#0F172A"
                />
                <MetricTile
                  label="Active Products"
                  value={manufacturingSummary.activeProducts}
                  helper="Manufacturable product definitions"
                  background="#F8FAFC"
                  color="#0F172A"
                />
                <MetricTile
                  label="Released Orders"
                  value={manufacturingSummary.releasedOrders}
                  helper="Orders already pushed to production"
                  background="#F8FAFC"
                  color="#0F172A"
                />
              </Box>

              <Divider />

              <Stack spacing={1}>
                {manufacturingSummary.latestOrders.length ? (
                  manufacturingSummary.latestOrders.map((order) => (
                    <Box
                      key={order.id}
                      sx={{
                        p: 1.3,
                        borderRadius: 3,
                        border: "1px solid rgba(226,232,240,0.85)",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{ color: "#0F172A", fontWeight: 800 }}
                          >
                            {order.order_code}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                          >
                            {order.partner?.name || "Partner pending"} -{" "}
                            {order.product?.name || "Product pending"}
                          </Typography>
                        </Box>
                        <Chip
                          label={order.status || "UNKNOWN"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: "#0F4C81",
                            backgroundColor: "#EFF6FF",
                          }}
                        />
                      </Stack>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#94A3B8" }}>
                    No manufacturing orders available yet.
                  </Typography>
                )}
              </Stack>
            </Stack>
          ) : (
            <Typography sx={{ color: "#94A3B8" }}>
              Manufacturing data is unavailable for this access scope.
            </Typography>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "projects") {
      return (
        <WidgetCard
          title="Project Rollouts"
          subtitle="Commercial deployment structure for customers, rollout programs, and site readiness."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/projects/overview")}
                sx={{ textTransform: "none", fontWeight: 700, color: "#fff" }}
              >
                Open projects
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={112} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          ) : data.projects ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.25,
                }}
              >
                {[
                  [
                    "Customers",
                    projectsSummary.customers,
                    "#EFF6FF",
                    "#1D4ED8",
                  ],
                  ["Projects", projectsSummary.projects, "#ECFDF3", "#15803D"],
                  ["Sites", projectsSummary.sites, "#FFF7ED", "#C2410C"],
                  [
                    "Planned Devices",
                    projectsSummary.plannedDevices,
                    "#F5F3FF",
                    "#6D28D9",
                  ],
                ].map(([label, value, bg, tone]) => (
                  <Box
                    key={label}
                    sx={{ p: 1.4, borderRadius: 3, backgroundColor: bg }}
                  >
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.9,
                        fontSize: 28,
                        fontWeight: 900,
                        color: tone,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr", // 📱 mobile → 1 column
                    sm: "1fr 1fr", // tablet → 2 columns
                    md: "1fr 1fr", // desktop → 2 columns
                  },
                  gap: 1.25,
                }}
              >
                <MetricTile
                  label="Active Projects"
                  value={projectsSummary.activeProjects}
                  helper="Programs currently running"
                  background="#F8FAFC"
                  color="#0F172A"
                />
                <MetricTile
                  label="Ready Sites"
                  value={projectsSummary.readySites}
                  helper="Sites prepared or under execution"
                  background="#F8FAFC"
                  color="#0F172A"
                />
                <MetricTile
                  label="Commissioned"
                  value={projectsSummary.commissionedDevices}
                  helper="Devices already marked live"
                  background="#F8FAFC"
                  color="#0F172A"
                />
              </Box>

              <Divider />

              <Stack spacing={1}>
                {projectsSummary.latestProjects.length ? (
                  projectsSummary.latestProjects.map((project) => (
                    <Box
                      key={project.id}
                      sx={{
                        p: 1.3,
                        borderRadius: 3,
                        border: "1px solid rgba(226,232,240,0.85)",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{ color: "#0F172A", fontWeight: 800 }}
                          >
                            {project.name}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.25, color: "#64748B", fontSize: 12 }}
                          >
                            {project.customer?.name || "Customer pending"} -{" "}
                            {project.target_devices || 0} target devices
                          </Typography>
                        </Box>
                        <Chip
                          label={project.status || "UNKNOWN"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            color: "#0F4C81",
                            backgroundColor: "#EFF6FF",
                          }}
                        />
                      </Stack>
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#94A3B8" }}>
                    No deployment projects available yet.
                  </Typography>
                )}
              </Stack>
            </Stack>
          ) : (
            <Typography sx={{ color: "#94A3B8" }}>
              Project rollout data is unavailable for this access scope.
            </Typography>
          )}
        </WidgetCard>
      );
    }

    if (widgetId === "activity") {
      return (
        <WidgetCard
          title="Recent Activity"
          subtitle="Latest operational actions flowing through the workspace."
          action={
            <Stack direction="row" spacing={1}>
              {dragBadge}
              <Button
                variant="text"
                endIcon={<ArrowOutwardRounded />}
                onClick={() => navigate("/audit_logs")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#fff",
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                Open logs
              </Button>
            </Stack>
          }
        >
          {loading ? (
            <Stack spacing={1.2}>
              {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rounded" height={64} />
              ))}
            </Stack>
          ) : data.audit.length === 0 ? (
            <Typography sx={{ color: "#94A3B8" }}>
              No recent activity available for your current scope.
            </Typography>
          ) : (
            <Stack spacing={1.15}>
              {data.audit.map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    border: "1px solid rgba(226,232,240,0.85)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#E0E7FF",
                        color: "#4338CA",
                        fontWeight: 800,
                      }}
                    >
                      {(log.action || "A").slice(0, 1)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "#0F172A",
                          fontWeight: 800,
                          wordBreak: "break-word",
                        }}
                      >
                        {log.action} - {log.entity_type}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: "#64748B",
                          fontSize: 12,
                          wordBreak: "break-word",
                        }}
                      >
                        {log.email || "Unknown actor"} -{" "}
                        {formatRelative(log.created_at)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </WidgetCard>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        pt: { xs: 5, md: 3 },
        minHeight: "100%",
        background:
          "radial-gradient(circle at top right, rgba(37,99,235,0.08), transparent 18%), linear-gradient(180deg, #F5F8FC 0%, #EEF3F7 100%)",
      }}
    >
      {/* <Zoom /> */}
      <Box id="overview-zoom-surface">
        <Card
          sx={{
            mb: 3,
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid rgba(15,23,42,0.08)",
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
            boxShadow: "0 30px 80px rgba(15,23,42,0.18)",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, md: 3.25 } }}>
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", xl: "center" }}
            >
              <Box sx={{ maxWidth: 760 }}>
                {/* <Chip
                  icon={<WidgetsRounded fontSize="small" />}
                  label="Overview Workspace"
                  sx={{
                    mb: 2,
                    color: "#DBEAFE",
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontWeight: 700,
                  }}
                /> */}
                <Typography
                  sx={{
                    color: "#F8FAFC",
                    fontSize: { xs: 28, md: 40 },
                    fontWeight: 900,
                    letterSpacing: "-0.05em",
                    lineHeight: 0.98,
                  }}
                >
                  Build your Dashboard
                </Typography>
                <Typography
                  sx={{
                    mt: 1.6,
                    color: "rgba(226,232,240,0.82)",
                    fontSize: 16,
                    lineHeight: 1.75,
                    maxWidth: 720,
                  }}
                >
                  Drag widgets into the order your team monitors every day. This
                  overview now pulls live fleet, inventory, rollout, and
                  activity data instead of showing placeholder blocks.
                </Typography>
              </Box>
              <Stack spacing={1.25} sx={{ width: { xs: "100%", xl: 380 } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 4,
                    backgroundColor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: "rgba(226,232,240,0.72)",
                          fontSize: 12, 
                          fontWeight: 700,
                        }}
                      >
                        Active workspace
                      </Typography>
                      <Typography
                        sx={{ mt: 0.75, color: "#F8FAFC", fontWeight: 800 }}
                      >
                        {activeOrg?.name || "Global workspace"}
                      </Typography>
                    </Box>
                    <Chip
                      label={editMode ? "Layout mode" : "Monitoring mode"}
                      sx={{
                        fontWeight: 700,
                        color: editMode ? "#FEF3C7" : "#DCFCE7",
                        backgroundColor: editMode
                          ? "rgba(234,179,8,0.14)"
                          : "rgba(34,197,94,0.12)",
                      }}
                    />
                  </Stack>
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                  <Button
                    fullWidth
                    variant={editMode ? "contained" : "outlined"}
                    startIcon={<EditRounded />}
                    onClick={() => setEditMode((prev) => !prev)}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 800,
                      ...(editMode
                        ? {
                            backgroundColor: "#F8FAFC",
                            color: "#0F172A",
                            "&:hover": { backgroundColor: "#E2E8F0" },
                          }
                        : {
                            borderColor: "rgba(255,255,255,0.18)",
                            color: "#F8FAFC",
                          }),
                    }}
                  >
                    {editMode ? "Done editing" : "Edit layout"}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<DashboardCustomizeRounded />}
                    onClick={() => setCustomizeOpen(true)}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: "rgba(255,255,255,0.18)",
                      color: "#F8FAFC",
                    }}
                  >
                    Customize
                  </Button>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshRounded />}
                    onClick={() => loadData(true)}
                    disabled={refreshing}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: "rgba(255,255,255,0.18)",
                      color: "#F8FAFC",
                    }}
                  >
                    {refreshing ? "Refreshing..." : "Refresh data"}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<SettingsBackupRestoreRounded />}
                    onClick={resetLayout}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: "rgba(255,255,255,0.18)",
                      color: "#F8FAFC",
                    }}
                  >
                    Reset layout
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // 📱 mobile → 1 column
              sm: "1fr 1fr", // tablet → 2 columns
              md: "1fr 1fr", // desktop → 2 columns
            },
            gap: 1.25,
          }}
        >
          {visibleWidgets.map((widget) => {
            const span = widget.span || 3;

            return (
              <Box
                key={widget.id}
                draggable={editMode}
                sx={{
                  minWidth: 0,
                  flex: {
                    xs: "1 1 100%", // 📱 mobile → full
                    sm: "1 1 calc(50% - 16px)", // 📱 tablet → 2
                    md: "1 1 calc(33.33% - 16px)", // 💻 → 3
                    lg: `1 1 calc(${(span / 12) * 100}% - 16px)`, // 🖥️ dynamic span
                  },
                  width: "100%",

                  cursor: editMode ? "grab" : "default",
                  transition: "all 0.2s ease",

                  "&:active": {
                    cursor: editMode ? "grabbing" : "default",
                  },
                }}
                onDragOver={(event) => {
                  if (!editMode) return;
                  event.preventDefault();
                }}
                onDrop={() => {
                  if (!editMode) return;
                  setWidgetOrder((prev) =>
                    reorder(prev, draggedWidgetId, widget.id),
                  );
                  setDraggedWidgetId("");
                }}
                onDragStart={() => {
                  if (!editMode) return;
                  setDraggedWidgetId(widget.id);
                }}
              >
                {renderWidget(widget.id)}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Dialog
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,

            boxShadow: "0 30px 80px rgba(15,23,42,0.18)",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            color: "#fff",
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {/* Left Content */}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoAwesomeRounded />
                <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                  Customize Dashboard
                </Typography>
              </Stack>

              <Typography sx={{ mt: 1, color: "#c0cbd9", fontSize: 14 }}>
                Control which widgets show up on the overview and reorder them
                in edit mode.
              </Typography>
            </Box>

            {/* Close Button */}
            <IconButton
              onClick={() => setCustomizeOpen(false)}
              sx={{
                color: "#fff",
                mt: 0.5,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={1.2}>
            {availableWidgets.map((widget) => {
              const hidden = hiddenWidgets.includes(widget.id);
              return (
                <Box
                  key={widget.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    border: "1px solid rgba(226,232,240,0.9)",
                    backgroundColor: "#fff",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography sx={{ color: "#0F172A", fontWeight: 800 }}>
                        {widget.title}
                      </Typography>
                      <Typography
                        sx={{ mt: 0.25, color: "#64748B", fontSize: 13 }}
                      >
                        {widget.description}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!hidden}
                          onChange={() => toggleWidget(widget.id)}
                        />
                      }
                      label={!hidden ? "Visible" : "Hidden"}
                      labelPlacement="start"
                    />
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={resetLayout}
            startIcon={<SettingsBackupRestoreRounded />}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Reset
          </Button>
          <Button
            onClick={() => setCustomizeOpen(false)}
            variant="contained"
            startIcon={<SaveRounded />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 3,
              background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
              "&:hover": {
                background: "linear-gradient(135deg, #FDE68A, #c08013)",
              },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
